import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import ResponsiveModal from "@/components/ResponsiveModal";
import { toast } from "sonner";
import { Send } from "lucide-react";

export default function SupportFeedbackDialog({ open, onOpenChange, defaultType = "support" }) {
  const [type, setType] = useState(defaultType);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { if (open) { setType(defaultType); setText(""); } }, [open, defaultType]);

  const submit = async () => {
    const msg = text.trim();
    if (!msg) { toast.error("Please write a message first."); return; }
    setSubmitting(true);
    try {
      await api.post("/support", { type, message: msg });
      toast.success(type === "feedback" ? "Thanks for the feedback — our team will review it." : "Support request sent — we'll get back to you soon.");
      onOpenChange?.(false);
    } catch (e) {
      toast.error("Could not send — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title="How can we help?"
      description="Contact support or share feedback"
      testid="support-dialog"
      className="max-w-md"
      footer={
        <>
          <Button variant="ghost" className="rounded-full" onClick={() => onOpenChange?.(false)} data-testid="support-cancel">Cancel</Button>
          <Button
            className="rounded-full bg-secondary hover:bg-secondary/90 text-secondary-foreground"
            onClick={submit}
            disabled={submitting || !text.trim()}
            data-testid="support-submit"
          >
            {submitting ? "Sending…" : <><Send size={15} className="mr-1" /> Send</>}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-2 mt-1">
        <button
          onClick={() => setType("support")}
          data-testid="support-type-support"
          className={`rounded-2xl border p-3 text-left transition active:scale-[0.98] ${type === "support" ? "border-secondary bg-secondary/10" : "border-border hover:bg-muted"}`}
        >
          <div className="text-sm font-medium text-primary">Get support</div>
          <div className="text-xs text-foreground/70 mt-0.5">Payment, access, or a problem.</div>
        </button>
        <button
          onClick={() => setType("feedback")}
          data-testid="support-type-feedback"
          className={`rounded-2xl border p-3 text-left transition active:scale-[0.98] ${type === "feedback" ? "border-secondary bg-secondary/10" : "border-border hover:bg-muted"}`}
        >
          <div className="text-sm font-medium text-primary">Share feedback</div>
          <div className="text-xs text-foreground/70 mt-0.5">An idea, suggestion, or what you love.</div>
        </button>
      </div>
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={type === "feedback" ? "Tell us what you think…" : "Describe your issue or question — include your payment reference if it's about a payment."}
        className="resize-none min-h-[120px] rounded-2xl bg-surface border-border mt-2 mb-1"
        data-testid="support-message-input"
      />
    </ResponsiveModal>
  );
}
